import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle } from "lucide-react";
import { CardDescription } from "@/components/ui/card";
import { CardHeader, CardTitle } from "@/components/ui/card";

interface TherapistVerificationFormProps {
  userData: {
    id: string;
    email: string;
    name: string;
    firstName: string;
    lastName: string;
    role: string;
    profileImageUrl: string;
    connectionKey: string;
    websiteUrl: string;
    showActivity: string;
    country: string;
    language: string;
    languageSecondary: string;
    profileBio: string;
  };
  userInitials: string;
}

const areaOfExpertiseOptions = [
  "Therapist",
  "Counsellor",
  "Psychologist",
  "Psychiatrist",
  "Psychotherapist",
  "Coach",
  "Other"
];

const TherapistVerificationForm: React.FC<TherapistVerificationFormProps> = ({
  userData,
  userInitials,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [formData, setFormData] = useState({
    areaOfExpertise: "",
    specialization: "",
    yearsOfExperience: 1,
    education: "",
    certifications: "",
    additionalNotes: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'yearsOfExperience' ? Number(value) : value
    }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      areaOfExpertise: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Insert verification data
      const { error: verificationError } = await supabase
        .from('therapist_verifications')
        .insert({
          user_id: userData.id,
          area_of_expertise: formData.areaOfExpertise,
          specialization: formData.specialization,
          years_of_experience: formData.yearsOfExperience,
          education: formData.education,
          certifications: formData.certifications,
          additional_notes: formData.additionalNotes,
          status: 'pending',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (verificationError) throw verificationError;

      toast.success("Verification submitted successfully!");
      setShowSuccessDialog(true);
    } catch (error) {
      console.error("Error submitting verification:", error);
      toast.error("Failed to submit verification. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="areaOfExpertise">Area of Expertise</Label>
            <Select
              value={formData.areaOfExpertise}
              onValueChange={handleSelectChange}
              required
            >
              <SelectTrigger className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8] h-10 text-sm" >
                <SelectValue placeholder="Select your area of expertise" />
              </SelectTrigger>
              <SelectContent>
                {areaOfExpertiseOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="specialization">Specialisation</Label>
            <Input
              id="specialization"
              name="specialization"
              value={formData.specialization}
              onChange={handleInputChange}
              placeholder="Enter your specialisation"
              required
              className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8] h-10 text-sm"
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="yearsOfExperience">Years of Experience</Label>
            <Input
              id="yearsOfExperience"
              name="yearsOfExperience"
              type="number"
              min={1}
              max={100}
              step={1}
              value={formData.yearsOfExperience}
              onChange={handleInputChange}
              required
              className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8] h-10 text-sm w-full md:w-32"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="education">Education</Label>
            <Textarea
              id="education"
              name="education"
              value={formData.education}
              onChange={handleInputChange}
              placeholder="List your educational background"
              required
              className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8] min-h-[60px] text-sm"
              maxLength={300}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="certifications">Certifications</Label>
            <Textarea
              id="certifications"
              name="certifications"
              value={formData.certifications}
              onChange={handleInputChange}
              placeholder="List your certifications and training"
              className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8] min-h-[60px] text-sm"
              maxLength={300}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="additionalNotes">Additional Notes</Label>
          <Textarea
            id="additionalNotes"
            name="additionalNotes"
            value={formData.additionalNotes}
            onChange={handleInputChange}
            placeholder="Any additional information you'd like to share"
            className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8] min-h-[60px] text-sm"
            maxLength={300}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="border-[#CCCCCC] hover:bg-[#E8F0F8] w-full md:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-[#3BAFDA] hover:bg-[#2a9fc4] text-white w-full md:w-auto"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Verification'
            )}
          </Button>
        </div>
      </form>

      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 bg-green-100 p-3 rounded-full w-fit">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <DialogTitle className="text-2xl text-center">Thank You!</DialogTitle>
            <DialogDescription className="text-center space-y-4">
              <div className="text-base">
                Your verification has been submitted successfully. The Clou Team will review your application and get back to you shortly.
              </div>
              <div className="text-sm text-muted-foreground">
                We typically review applications within 1-2 business days. You'll receive an email notification once your verification is complete.
              </div>
              <Button
                onClick={() => window.location.href = 'https://www.clamalo.com'}
                className="w-full bg-[#3BAFDA] hover:bg-[#2a9fc4] text-white"
              >
                Return to Clou
              </Button>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TherapistVerificationForm; 